import { Component, computed, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal, ViewChild } from "@angular/core";
import { AdminGridColumnsFactory } from "../admin-grid/admin-grid-columns-factory";
import { Column, SelectionChangedEvent } from "devextreme/ui/data_grid";
import { CustomItemCreatingEvent } from "devextreme/ui/select_box";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { combineLatest, map } from "rxjs";
import { ToolbarItem, ToolbarItemFactory } from "../toolbar";
import { DxDataGridComponent } from "devextreme-angular";
import { brdCatchError } from "../message-utils";
import { MetadataService } from "../../services/metadata.service";
import { DomainViewModel, MacrosectorViewModel, ProcessViewModel, UseCaseViewModel } from "../../core/model/ContentManagement";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "brd-usecases-grid",
  templateUrl: "./usecases-grid.component.html",
  styleUrl: "./usecases-grid.component.less",
  standalone: false
})
export class UsecasesGridComponent implements OnInit {
  @Input() selectedRowKey: number[] = [];
  @Input() enableDelete: boolean = false;
  @Input() customItems: ToolbarItem[] = [];
  @Output() selectionChanged = new EventEmitter<number[]>();
  @ViewChild(DxDataGridComponent) useCaseGrid!: DxDataGridComponent;

  destroyRef = inject(DestroyRef);

  visible = false;
  visibleDelete = false;
  columnsUseCases: Column[] = [];
  selectedUseCases: string[] = [];

  nameControl = new FormControl("", Validators.required);
  processControl = new FormControl<ProcessViewModel | null>(null, Validators.required);
  macrosectorControl = new FormControl<MacrosectorViewModel | null>(null, Validators.required);
  domainControl = new FormControl<DomainViewModel | null>(null, Validators.required);
  useCaseFormGroup = new FormGroup({
    id: new FormControl(0),
    name: this.nameControl,
    macrosector: this.macrosectorControl,
    domain: this.domainControl,
    process: this.processControl
  });
  saveBtnOptions = {
    text: "Save",
    onClick: (): void => this.confirmChanges()
  };
  cancelBtnOptions = {
    text: "Cancel",
    onClick: (): void => {
      this.hidePopup();
    }
  };

  deleteBtnOptions = {
    text: "Yes",
    onClick: (): void => {
      this.deleteUseCases();
    }
  };

  cancelDeleteBtnOptions = {
    text: "No",
    onClick: (): void => this.hideDeletePopup()
  };

  useCases$ = combineLatest([this.metadataService.useCases$, this.metadataService.filterUseCases$]).pipe(
    map(([useCases, filterIds]) => {
      if (!filterIds.length) return useCases;
      return useCases.filter(x => filterIds.includes(x.id));
    })
  );

  processes = signal<ProcessViewModel[]>([]);
  domains = signal<DomainViewModel[]>([]);
  macrosectors = signal<MacrosectorViewModel[]>([]);

  selectedProcess = signal<ProcessViewModel | null>(null);
  selectedDomain = signal<DomainViewModel | null>(null);
  selectedMacrosector = signal<MacrosectorViewModel | null>(null);

  filteredProcesses = computed(() => {
    if (this.selectedDomain()?.id) {
      return this.processes().filter(x => x.domainId === this.selectedDomain()!.id);
    } else if (this.selectedMacrosector()?.id) {
      return this.processes().filter(x => x.macrosectorId === this.selectedMacrosector()!.id);
    } else {
      return this.processes();
    }
  });

  filteredDomain = computed(() => {
    if (this.selectedMacrosector()?.id) {
      return this.domains().filter(x => x.macrosectorId === this.selectedMacrosector()!.id);
    } else {
      return this.domains();
    }
  });

  toolbarItems: ToolbarItem[] = [];

  constructor(private metadataService: MetadataService) {
    this.useCaseFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(formData => {
      this.selectedDomain.set(formData.domain ?? null);
      this.selectedProcess.set(formData.process ?? null);
      this.selectedMacrosector.set(formData.macrosector ?? null);

      if (formData.process && !formData.domain) {
        this.domainControl.setValue(this.domains().find(x => x.id === formData.process!.domainId)!);
      }

      if ((formData.process || formData.domain) && !formData.macrosector) {
        const macrosectorId = formData.domain?.macrosectorId ?? formData.process!.macrosectorId;
        this.macrosectorControl.setValue(this.macrosectors().find(x => x.id === macrosectorId)!);
      }
    });
  }

  ngOnInit(): void {
    combineLatest({
      processes: this.metadataService.processes$,
      domains: this.metadataService.domains$,
      macrosectors: this.metadataService.macrosectors$
    })
      .pipe(
        map(({ processes, domains, macrosectors }) => {
          this.processes.set(processes);
          this.domains.set(domains);
          this.macrosectors.set(macrosectors);

          this.columnsUseCases = [
            AdminGridColumnsFactory.createTextColumn<UseCaseViewModel>(
              { fieldName: "id", caption: "id" },
              { visible: false, allowEditing: false }
            ),
            AdminGridColumnsFactory.createTextColumn({ fieldName: "name", caption: "Name" }),
            AdminGridColumnsFactory.createLookupColumn({
              fieldName: "processId",
              caption: "Process",
              lookup: { dataSource: processes, displayExpr: "name", valueExpr: "id" }
            }),
            AdminGridColumnsFactory.createLookupColumn({
              fieldName: "domainId",
              caption: "Domain",
              lookup: { dataSource: domains, displayExpr: "name", valueExpr: "id" }
            }),
            AdminGridColumnsFactory.createLookupColumn({
              fieldName: "macrosectorId",
              caption: "Macrosector",
              lookup: { dataSource: macrosectors, displayExpr: "name", valueExpr: "id" }
            }),
            AdminGridColumnsFactory.createButtonsColumn({
              buttons: [
                {
                  icon: "edit",
                  onClick: (e): void => {
                    this.showPopup({ ...e.row!.data });
                  }
                }
              ]
            })
          ] as Column[];
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.toolbarItems = [
      ToolbarItemFactory.createButton({
        icon: "add",
        text: "Use case",
        onClick: () => {
          this.addRow();
        }
      }),
      ...this.customItems
    ];
  }

  onSelectionChanged(e: SelectionChangedEvent): void {
    if (this.enableDelete) {
      if (e.selectedRowKeys.length > 0 && this.toolbarItems.length === 1) {
        this.toolbarItems.push(
          ToolbarItemFactory.createButton({
            icon: "trash",
            text: "Delete",
            type: "danger",
            onClick: () => {
              this.selectedUseCases = this.useCaseGrid.instance.getSelectedRowsData().map(x => x.name);
              this.visibleDelete = true;
            }
          })
        );
      } else if (e.selectedRowKeys.length === 0 && this.toolbarItems.length > 1) {
        this.toolbarItems = this.toolbarItems.slice(0, 1);
      }
    }

    this.selectionChanged?.emit(e.selectedRowKeys);
  }

  addRow(): void {
    this.showPopup();
  }

  showPopup(formData?: { id: number; name: string; domainId: number; processId: number; macrosectorId: number }): void {
    this.useCaseFormGroup.setValue({
      id: formData?.id ?? 0,
      name: formData?.name ?? "",
      macrosector: formData?.macrosectorId ? this.macrosectors().find(m => m.id === formData.macrosectorId)! : null,
      domain: formData?.domainId ? this.domains().find(d => d.id === formData.domainId)! : null,
      process: formData?.processId ? this.processes().find(p => p.id === formData.processId)! : null
    });
    this.visible = true;
  }

  hidePopup = (): void => {
    this.useCaseFormGroup.reset();
    this.visible = false;
  };

  hideDeletePopup = (): void => {
    this.visibleDelete = false;
  };

  onCustomItemCreating(e: CustomItemCreatingEvent): void {
    if (e.text) {
      const newItem = { id: 0, name: e.text };
      e.customItem = newItem;
    }
  }

  confirmChanges(): void {
    if (this.useCaseFormGroup.invalid) {
      this.useCaseFormGroup.markAllAsTouched();
      return;
    }

    const { id, name, domain, process, macrosector } = this.useCaseFormGroup.value as {
      id: number;
      name: string;
      domain: DomainViewModel;
      process: ProcessViewModel;
      macrosector: MacrosectorViewModel;
    };
    this.metadataService
      .addUseCase({
        id,
        name,
        domainId: domain.id,
        domainName: domain.name,
        processId: process.id,
        processName: process.name,
        macrosectorId: macrosector.id,
        macrosectorName: macrosector.name
      })
      .pipe(
        map(res => {
          this.hidePopup();
          return res;
        }),
        brdCatchError()
      )
      .subscribe();
  }

  deleteUseCases(): void {
    const useCaseIds = this.useCaseGrid.instance.getSelectedRowKeys();

    this.metadataService
      .deleteUseCases(useCaseIds)
      .pipe(
        map(res => {
          this.visibleDelete = false;
          return res;
        })
      )
      .subscribe();
  }
}
